import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  merge,
  switchMap,
  map,
  catchError
} from 'rxjs/operators';
import { NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { EnterpriseSearchService } from '@sabstravtech/obtservices/angular';

import { ChatWidgetInputBase } from '../chat-widget-input-base';

export interface ChatLoyaltyChoice {
  title: string;
  value: string;
}

export interface ChatLoyaltyAnswer {
  programme: string;
  programmeTitle: string;
  number: string;
}

/**
 * Renders the loyalty-programme picker widget inside the chat widget: an
 * airline typeahead (type the first few letters, pick from a filtered list
 * shown as "British Airways (BA)") plus a membership-number text field.
 * Looks airlines up live via `EnterpriseSearchService.getAirlinesList()` —
 * the exact same server-side search the manual booking form's own
 * schema-airline-autosuggest component uses — rather than requiring
 * booking_info's get result to embed the full ~700-airline list (that list
 * alone was costing ~9k LLM tokens per lookup; the FE no longer needs it at
 * all now). Mirrors the phone-number/passport widgets' locked/read-only +
 * showLabel/Skip behaviour exactly.
 */
@Component({
  selector: 'app-chat-loyalty-input',
  standalone: false,
  templateUrl: './chat-loyalty-input.component.html',
  styleUrls: ['./chat-loyalty-input.component.scss']
})
export class ChatLoyaltyInputComponent extends ChatWidgetInputBase implements OnChanges {
  @Input() passengerName = '';
  // Deliberately typed as the raw record (not a pre-cast interface) and
  // cast defensively inside ngOnChanges instead — the parent must pass the
  // SAME object reference across change-detection cycles (e.g.
  // `passenger.savedValue` directly) rather than constructing a new object
  // literal on every template check, otherwise `ngOnChanges` re-fires on
  // every keystroke and resets whatever the user is currently typing.
  @Input() savedValue: Record<string, unknown> | null = null;
  @Input() required?: boolean;
  @Output() answer = new EventEmitter<ChatLoyaltyAnswer>();
  @Output() skip = new EventEmitter<void>();

  constructor(private searchService: EnterpriseSearchService) {
    super();
  }

  focus$ = new Subject<string>();

  // Bound via [(ngModel)] on the typeahead input itself — this is the
  // officially-supported way to pair ngbTypeahead with a value (every
  // ng-bootstrap example, and the manual booking form's own airline
  // autosuggest, pairs it with formControlName/ngModel). ngbTypeahead
  // registers itself as the input's ControlValueAccessor, so ngModel
  // (not manual ViewChild DOM writes) is what keeps the displayed text in
  // sync with `selectedChoice` — holds either a ChatLoyaltyChoice (once
  // selected) or a raw string (while the user is still typing).
  airlineModel: ChatLoyaltyChoice | string | null = null;
  selectedChoice: ChatLoyaltyChoice | null = null;
  loyaltyNumber = '';
  /** Human-readable summary of what was submitted (via Continue or Skip),
   * shown in place of the controls once this widget instance is locked. */
  chosenSummary: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    // Reset the picker whenever the passenger changes (new question) — but
    // NOT merely because `locked` toggled, which would wipe out the
    // selection just as we want to display it as read-only.
    if (this.onlyLockedChanged(changes)) {
      return;
    }
    this.loyaltyNumber = this.savedLoyaltyNumber ?? '';
    this.chosenSummary = null;
    const code = this.savedProgrammeCode;
    if (!code) {
      this.selectedChoice = null;
      this.airlineModel = null;
      return;
    }
    // No static choices list to look the title up in any more — resolve it
    // live via the same airline search endpoint (same pattern as the manual
    // booking form's schema-airline-autosuggest.component.ts). Falls back to
    // showing the bare code if the lookup fails/finds nothing.
    this.selectedChoice = { title: code, value: code };
    this.airlineModel = this.selectedChoice;
    this.searchService
      .getAirlinesList(code)
      .pipe(catchError(() => of([])))
      .subscribe((airlines) => {
        const match = (airlines ?? []).find((a) => a.code === code);
        if (match && this.savedProgrammeCode === code) {
          this.selectedChoice = { title: match.name, value: match.code };
          this.airlineModel = this.selectedChoice;
        }
      });
  }

  private get savedProgrammeCode(): string | null {
    const programme = this.savedValue?.['programme'];
    return typeof programme === 'string' ? programme : null;
  }

  private get savedLoyaltyNumber(): string | null {
    const number = this.savedValue?.['number'];
    return typeof number === 'string' ? number : null;
  }

  get isMandatory(): boolean {
    return this.required === true;
  }

  get isValid(): boolean {
    if (this.selectedChoice && this.loyaltyNumber.trim()) {
      return true;
    }
    return !this.isMandatory;
  }

  formatterAirlines = (choice: ChatLoyaltyChoice): string => `${choice.title} (${choice.value})`;

  // Same shape as the manual booking form's typeahead (schema-airline-autosuggest
  // .component.ts) — debounce + distinctUntilChanged + refire on focus — and now
  // calls the exact same live `getAirlinesList()` server-side search instead of
  // filtering a preloaded list, so no airline data ever needs to flow through the
  // LLM tool response for this widget to work.
  airline = (text$: Observable<string>): Observable<ChatLoyaltyChoice[]> =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      merge(this.focus$),
      switchMap((term: string): Observable<ChatLoyaltyChoice[]> => {
        if (!term || term.length <= 1) {
          return of([]);
        }
        return this.searchService.getAirlinesList(term).pipe(
          map((airlines) =>
            (airlines ?? [])
              .filter((a) => !!a?.code && !!a?.name)
              .map((a) => ({ title: a.name, value: a.code }))
              .slice(0, 20)
          ),
          catchError(() => of([]))
        );
      })
    );

  onAirlineSelected(event: NgbTypeaheadSelectItemEvent<ChatLoyaltyChoice>): void {
    this.selectedChoice = event.item;
  }

  onContinue(): void {
    if (this.locked || !this.isValid) {
      return;
    }
    if (this.selectedChoice && this.loyaltyNumber.trim()) {
      this.chosenSummary = `${this.selectedChoice.title} - ${this.loyaltyNumber.trim()}`;
      this.answer.emit({
        programme: this.selectedChoice.value,
        programmeTitle: this.selectedChoice.title,
        number: this.loyaltyNumber.trim()
      });
      return;
    }
    // Only reachable for an optional field with nothing entered.
    this.chosenSummary = 'Skipped';
    this.skip.emit();
  }

  onSkip(): void {
    if (this.locked) {
      return;
    }
    this.chosenSummary = 'Skipped';
    this.skip.emit();
  }
}
